import { useCallback, useMemo } from 'react'
import siteMap from '../../../siteMap'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { Card, CardContent, Chip, Grid, Stack, Typography, useTheme, Button } from '@mui/material'
import { DataGrid, GridColDef, GridToolbarContainer, useGridApiContext } from '@mui/x-data-grid'
import { useLocations } from '../../../hooks/locations'
import { usePortfolioClimateData } from '../../../hooks/climateData'
import { HeatStressScoreBadge } from '../common/heatStress/utils'
import {
  EmissionTier,
  MetricState,
  stateColorMapping,
  CanonicalPeriod,
  canonicalPeriodLabels,
  metricStateLabels,
  PERIODS,
  getPeriodYear,
} from '../common'
import { useHistory } from 'react-router-dom'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import { LoadingValue } from './PortfolioLoading'

function formatNumber(n: number, fraction: number = 1) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: fraction }).format(n)
}

function NumberCell({ value, loading }: { value?: number | null; loading?: boolean }) {
  return (
    <LoadingValue loading={loading} width={70} fallback="—">
      {typeof value === 'number' ? formatNumber(value) : null}
    </LoadingValue>
  )
}

function StatusChip({ value }: { value?: MetricState | null }) {
  if (!value) return <span>—</span>
  const label = metricStateLabels[value] || value
  const color = stateColorMapping[value]
  return (
    <span style={{ background: color, color: 'white', fontSize: 11, padding: '2px 6px', borderRadius: 4 }}>
      {label}
    </span>
  )
}

function PropertyFilterToolbar() {
  const apiRef = useGridApiContext()
  return (
    <GridToolbarContainer sx={{ justifyContent: 'space-between', px: 0, py: 1 }}>
      <Typography variant="subtitle1">Property Breakdown</Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterAltIcon />}
          onClick={() => apiRef.current?.showFilterPanel?.()}>
          Filter
        </Button>
      </Stack>
    </GridToolbarContainer>
  )
}

type Row = {
  id: number
  name: string
  country?: string
  statusHeatScore?: MetricState
  statusHotDays?: MetricState
  heatScore: Partial<Record<CanonicalPeriod, number | null>>
  hotDays: Partial<Record<CanonicalPeriod, number | null>>
}

export default function PortfolioHeatStress({ emissionTier }: { emissionTier: EmissionTier }) {
  const theme = useTheme()
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations, isLoading: locationsLoading } = useLocations({ group_id: groupId })
  const { data: hotDaysByProperty, isLoading: hotDaysLoading } = usePortfolioClimateData('hs:hot_days', {
    groupId: groupId ?? undefined,
  })
  const { data: heatScoresByProperty, isLoading: heatScoresLoading } = usePortfolioClimateData('hs:heat_scores', {
    groupId: groupId ?? undefined,
  })

  const goToProperty = useCallback(
    (propertyId: number) => history.push(siteMap.PropertyClimateAnalytics.getPath(propertyId, 'hs', emissionTier)),
    [emissionTier, history],
  )

  const rows: Row[] = useMemo(() => {
    const base: Record<number, Row> = {}
    ;(locations || []).forEach((property: any) => {
      base[property.id] = {
        id: property.id,
        name: property.name || `Property ${property.id}`,
        country: property.address?.country,
        heatScore: {},
        hotDays: {},
      }
    })

    const tierKey = emissionTier as unknown as string // 'low' | 'moderate' | 'high'

    function pickY(entry: any, period: CanonicalPeriod): number | null {
      if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
      return entry.data?.data?.[tierKey]?.[period]?.y ?? null
    }

    if (heatScoresByProperty && typeof heatScoresByProperty === 'object') {
      Object.values(heatScoresByProperty as Record<string, any>).forEach(entry => {
        const r = base[entry.property_id] || {
          id: entry.property_id,
          name: `Property ${entry.property_id}`,
          heatScore: {},
          hotDays: {},
        }
        r.statusHeatScore = entry.state?.value
        PERIODS.forEach(p => (r.heatScore[p] = pickY(entry, p)))
        base[entry.property_id] = r
      })
    }

    if (hotDaysByProperty && typeof hotDaysByProperty === 'object') {
      Object.values(hotDaysByProperty as Record<string, any>).forEach(entry => {
        const r = base[entry.property_id] || {
          id: entry.property_id,
          name: `Property ${entry.property_id}`,
          heatScore: {},
          hotDays: {},
        }
        r.statusHotDays = entry.state?.value
        PERIODS.forEach(p => (r.hotDays[p] = pickY(entry, p)))
        base[entry.property_id] = r
      })
    }

    return Object.values(base)
  }, [locations, emissionTier, heatScoresByProperty, hotDaysByProperty])

  // KPIs (per period): average heat score, average hot days, coverage for each metric
  const kpis = useMemo(() => {
    type Agg = {
      avgHeatScore: number | null
      avgHotDays: number | null
      coverageHeatScore: number
      coverageHotDays: number
    }
    const out: Record<CanonicalPeriod, Agg> = {
      [CanonicalPeriod.SHORT_TERM]: { avgHeatScore: null, avgHotDays: null, coverageHeatScore: 0, coverageHotDays: 0 },
      [CanonicalPeriod.MID_TERM]: { avgHeatScore: null, avgHotDays: null, coverageHeatScore: 0, coverageHotDays: 0 },
      [CanonicalPeriod.LONG_TERM]: { avgHeatScore: null, avgHotDays: null, coverageHeatScore: 0, coverageHotDays: 0 },
    }
    PERIODS.forEach(p => {
      const heatVals = rows.map(r => r.heatScore[p]).filter((v): v is number => typeof v === 'number')
      const daysVals = rows.map(r => r.hotDays[p]).filter((v): v is number => typeof v === 'number')
      const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null)
      out[p] = {
        avgHeatScore: mean(heatVals),
        avgHotDays: mean(daysVals),
        coverageHeatScore: heatVals.length,
        coverageHotDays: daysVals.length,
      }
    })
    return out
  }, [rows])

  const gridLoading = locationsLoading && locations.length === 0

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      { field: 'name', headerName: 'Property', width: 240 },
      { field: 'country', headerName: 'Country', width: 130 },
      ...PERIODS.map(
        (period): GridColDef<Row> => ({
          field: `heat_score_${period}`,
          headerName: `Risk Rating (${getPeriodYear(period)})`,
          width: 170,
          valueGetter: params => {
            const v = params.row.heatScore[period]
            return typeof v === 'number' ? v : null
          },
          renderCell: params => (
            <LoadingValue loading={heatScoresLoading} variant="rounded" width={72} height={20}>
              <HeatStressScoreBadge score={(params.value as number | null) ?? null} size="dense" />
            </LoadingValue>
          ),
        }),
      ),
      ...PERIODS.map(
        (period): GridColDef<Row> => ({
          field: `hot_days_${period}`,
          headerName: `Hot Days (${getPeriodYear(period)})`,
          width: 140,
          valueGetter: params => {
            const v = params.row.hotDays[period]
            return typeof v === 'number' ? Math.round(v) : null
          },
          renderCell: params => <NumberCell value={(params.value as number | null) ?? null} loading={hotDaysLoading} />,
        }),
      ),
      {
        field: 'statusHeatScore',
        headerName: 'Rating Status',
        width: 160,
        sortable: false,
        valueGetter: p => p.row.statusHeatScore ?? null,
        renderCell: p => (
          <LoadingValue loading={heatScoresLoading} variant="rounded" width={80} height={18}>
            <StatusChip value={p.value as MetricState | null} />
          </LoadingValue>
        ),
      },
      {
        field: 'statusHotDays',
        headerName: 'Hot Days Status',
        width: 160,
        sortable: false,
        valueGetter: p => p.row.statusHotDays ?? null,
        renderCell: p => (
          <LoadingValue loading={hotDaysLoading} variant="rounded" width={80} height={18}>
            <StatusChip value={p.value as MetricState | null} />
          </LoadingValue>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: params => (
          <Button size="small" onClick={() => goToProperty(params.row.id)}>
            View property
          </Button>
        ),
      },
    ],
    [goToProperty, heatScoresLoading, hotDaysLoading],
  )

  return (
    <Stack alignItems={'center'} pb={6}>
      <Stack sx={{ maxWidth: theme.breakpoints.values?.['lg'], width: '100%' }} spacing={2}>
        {/* KPI strip */}
        <Grid container spacing={2}>
          {/* Average Heat Score */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="overline">Average Risk Rating</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  {PERIODS.map(period => (
                    <Stack key={period} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">{canonicalPeriodLabels[period]}</Typography>
                      <LoadingValue loading={heatScoresLoading} variant="rounded" width={72} height={20}>
                        <HeatStressScoreBadge score={kpis[period].avgHeatScore} size="dense" />
                      </LoadingValue>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Average Hot Days */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="overline">Average # of Local Hot Days</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  {PERIODS.map(period => (
                    <Stack key={period} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">{canonicalPeriodLabels[period]}</Typography>
                      <LoadingValue loading={hotDaysLoading} variant="rounded" width={120} height={24}>
                        <Chip
                          label={
                            kpis[period].avgHotDays != null
                              ? `${formatNumber(kpis[period].avgHotDays!, 0)} days/year`
                              : '—'
                          }
                        />
                      </LoadingValue>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tables */}
        <Card>
          <CardContent>
            <div style={{ height: 520, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={r => r.id}
                loading={gridLoading}
                initialState={{
                  sorting: { sortModel: [{ field: `heat_score_${CanonicalPeriod.LONG_TERM}`, sort: 'desc' }] },
                  columns: {
                    columnVisibilityModel: {
                      statusHeatScore: false,
                      statusHotDays: false,
                    },
                  },
                }}
                density="compact"
                onRowDoubleClick={params => goToProperty(params.row.id)}
                disableSelectionOnClick
                components={{ Toolbar: PropertyFilterToolbar }}
                sx={{
                  '& .MuiDataGrid-row': { cursor: 'pointer' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: theme.palette.action.hover },
                }}
              />
            </div>
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  )
}
